import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { GrundstueckId } from '../../../core/gb-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import {
  AvgeometergeschaeftService,
  GeometergeschaeftListEntryMeta,
  LoadGeoGeschaeftSichtByGrundstueckIdsRequest
} from '../../../core/av-api';
import { SortDirection, TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { keysToTranslate } from '../../../../i18n/keys-to-translate';
import { ListLoaderPaginationBuilder } from '../../../shared/components/list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../shared/components/list-loader/pagination-lookup-result';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { KompaktGeometergeschaeftComponent } from '../../../shared/kompaktdarstellung/geometergeschaeft/kompakt-geometergeschaeft.component';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import SortEnum = LoadGeoGeschaeftSichtByGrundstueckIdsRequest.SortEnum;

@Component({
  selector: 'cap-haengige-geo-geschaefte-icp',
  imports: [
    ColumnHeaderComponent,
    DatumPipe,
    KompaktGeometergeschaeftComponent,
    LinkComponent,
    ListLoaderWrapperComponent,
    TranslatePipe,
    NgTemplateOutlet
  ],
  templateUrl: './haengige-geo-geschaefte-icp.component.html'
})
export class HaengigeGeoGeschaefteIcpComponent extends AbstractComponent implements OnDestroy, OnChanges {
  readonly PAGE_SIZE = 5;
  readonly CURRENT_PAGE = 1;

  @Input()
  perimeter: Array<GrundstueckId>;
  @Input()
  hasRight = true;

  haengigeLoader: ListLoader<GeometergeschaeftListEntryMeta>;
  tableSortModel = new TableSortModel<GeometergeschaeftListEntryMeta>('table', this.comparatorService);
  private _loadOption;

  constructor(
    private geometergeschaeftService: AvgeometergeschaeftService,
    private comparatorService: ComparatorService
  ) {
    super();
    keysToTranslate<string>(['AvMutation', 'DatumAuftrag']);
    keysToTranslate<GeometergeschaeftListEntryMeta.StatusEnum>([
      'EROEFFNET',
      'BEARBEITUNG',
      'BEREIT_ZUR_VERIFIKATION',
      'WARTEN_AUF_VORGAENGER',
      'BEREIT_FUER_GRUNDBUCH',
      'RECHTSGUELTIG_VOLLZOGEN',
      'RECHTSGUELTIG_ABGESCHLOSSEN',
      'ZURUECK_ZUR_KORREKTUR',
      'ZURUECK_ZURUECKGEZOGEN',
      'ZURUECK_ABGEWIESEN',
      'RUECKMUTIERT_ABGESCHLOSSEN',
      'BEREIT_FUER_VERARBEITUNG',
      'VERKNUEPFT_MIT_GRUNDBUCH',
      'BEREIT_FUER_KORREKTURLIEFERUNG'
    ]);
    this.tableSortModel.addColumn('nummer', undefined, SortEnum.NUMMER).changeSortMode(SortDirection.DESC);
    this.tableSortModel.addColumn('status', undefined, SortEnum.STATUS);
    this.tableSortModel.addColumn('auftragsDatum', undefined, SortEnum.AUFTRAG_DATUM);
    this.tableSortModel.addColumn('avMutation', undefined, SortEnum.AV_MUTATION);
    this.tableSortModel.addColumn('geoGeschaeftsArt', undefined, SortEnum.GESCHAEFT_ART);
  }

  get loadOption() {
    return this._loadOption;
  }

  @Input()
  set loadOption(value: string) {
    if (!value) {
      return;
    }
    this._loadOption = value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.perimeter?.currentValue || this.perimeter) {
      this.updateLoader();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
  }

  private updateLoader() {
    if (!this.hasRight || !this.perimeter) {
      return;
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
    if (this.perimeter.length === 1) {
      this.haengigeLoader = this.createGeoGeschaeftByGrundstueckIdLoader();
    } else {
      this.haengigeLoader = this.createGeoGeschaeftByGrundstueckIdsLoader();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.applySortModel(this.tableSortModel);
    }
  }

  private createGeoGeschaeftByGrundstueckIdLoader(): ListLoader<GeometergeschaeftListEntryMeta> {
    const observable = (offset, limit, sortColumn) => this.createGeoGeschaeftByGrundstueckIdObservable(offset, limit, sortColumn);
    const loader = ListLoaderPaginationBuilder.builder<GeometergeschaeftListEntryMeta>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGeoGeschaeftByGrundstueckIdObservable(offset, limit, sortColumn) {
    return this.geometergeschaeftService
      .loadGeometergeschaeftSichtGrundstueckGet(
        this.perimeter[0].id,
        offset,
        limit,
        sortColumn.sortDirectionBackend,
        sortColumn.sortEnumBackend,
        this._loadOption
      )
      .pipe(
        map(
          (r) =>
            <PaginationLookupResult<GeometergeschaeftListEntryMeta>>{
              result: r.results,
              totalItems: r.totalItems
            }
        )
      );
  }

  private createGeoGeschaeftByGrundstueckIdsLoader(): ListLoader<GeometergeschaeftListEntryMeta> {
    const observable = (offset, limit, sortColumn) => this.createGeoGeschaeftByGrundstueckIdsObservable(offset, limit, sortColumn);
    const loader = ListLoaderPaginationBuilder.builder<GeometergeschaeftListEntryMeta>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGeoGeschaeftByGrundstueckIdsObservable(offset, limit, sortColumn) {
    const request: LoadGeoGeschaeftSichtByGrundstueckIdsRequest = {
      grundstueckIds: this.perimeter,
      auftragLoadOption: 'HAENGIGE',
      offset: offset,
      limit: limit,
      sortDirection: sortColumn.sortDirectionBackend,
      sort: sortColumn.sortEnumBackend
    };
    return this.geometergeschaeftService.loadGeometergeschaeftSichtGrundstueckPost(request).pipe(
      map(
        (r) =>
          <PaginationLookupResult<GeometergeschaeftListEntryMeta>>{
            result: r.results,
            totalItems: r.totalItems
          }
      )
    );
  }
}
